// The pure half of the PostHog browser wiring. It touches no globals and imports nothing but its
// equally pure sibling (every collaborator arrives as an argument), so it runs unchanged under
// `node --test` (packs/assets/spec/javascript/analytics/posthog_pageviews.test.js). That is what
// turns "exactly one $pageview per Turbo navigation" into an assertion instead of a hope.
//
// posthog.js is the other half: it owns the posthog-js import and hands this module the functions
// it calls.

// Re-exported so a caller reads the whole init contract off one module, and imported because
// `postHogInitOptions` below builds it in (analytics/session_replay.js explains every line of it).
import { SESSION_RECORDING_OPTIONS } from "./session_replay.js"

export { SESSION_RECORDING_OPTIONS }

// The marketing layout renders this element (analytics/_post_hog_settings.html.erb) whenever
// PostHogConfig says analytics is on. Since 12.6 the setup wizard and the settings screens render
// their own copy of it (analytics/_post_hog_app.html.erb), which carries the replay marker as well.
// No other signed-in page renders either one (doc/product-analytics.md §6).
export const POST_HOG_SETTINGS_SELECTOR = "[data-post-hog-api-key]"

// Pins posthog-js's default behaviours to a dated baseline, so a later SDK upgrade cannot
// silently start capturing something we did not agree to. Moving this date is a deliberate
// decision with its own PR (doc/product-analytics.md §5, A1).
export const POST_HOG_SDK_DEFAULTS_BASELINE = "2026-05-30"

// Marks the object we attached the turbo:load listener to. The marker lives on the shared event
// target (the document) rather than in a module variable on purpose: a second evaluation of this
// module gets fresh module state but the same document, and the document is the only place the
// first evaluation can leave a note the second one will read.
export const POST_HOG_LISTENER_MARKER = "postHogPageviewsListenerRegistered"

export function readPostHogSettings(documentRoot) {
  const element = documentRoot.querySelector(POST_HOG_SETTINGS_SELECTOR)
  if (!element) return null

  return {
    apiKey: element.dataset.postHogApiKey,
    host: element.dataset.postHogHost,
    // Absent on every marketing page, and present only on the two signed-in areas the server
    // allows a recording on (doc/product-analytics.md §6, B2). Compared against the string rather
    // than read as truthy, so a mistyped attribute reads as "no" instead of as "yes".
    sessionReplay: element.dataset.postHogSessionReplay === "true",
  }
}

export function postHogInitOptions({ host }) {
  return {
    api_host: host,
    // NOTHING IS EVER RECORDED BY DEFAULT, on any page, including the marketing site. Replay is
    // started by hand on the pages that allow it and stopped again on every other navigation
    // (analytics/session_replay.js), so "which screens may be recorded" is one decision in one
    // place rather than an option that has to be got right twice.
    disable_session_recording: true,
    session_recording: SESSION_RECORDING_OPTIONS,
    defaults: POST_HOG_SDK_DEFAULTS_BASELINE,
    // Person profiles cost an event's worth of quota each and only earn their keep once
    // someone is identified. Anonymous marketing traffic stays anonymous.
    person_profiles: "identified_only",
    // Nothing is written to the device until a visitor explicitly consents. Until then PostHog
    // derives a daily-rotating id server-side, which is what lets us collect pageviews before
    // the consent banner (A2) exists without a consent surface at all.
    cookieless_mode: "on_reject",
    // Load-bearing alongside cookieless_mode, and the reason is not obvious. posthog-js gates
    // every capture on is_capturing(), which under cookieless_mode "on_reject" reduces to
    // "consent.isRejected() || consent.isOptedIn()". A visitor who has never chosen is neither:
    // isOptedIn() is false because "no stored consent plus on_reject" is itself an opted-out
    // state, and isRejected() is false unless this option says the default is to reject. So
    // without this line no visitor is ever captured until someone clicks accept -- and until A2
    // ships there is nothing to click. Setting it puts a pending visitor in the rejected-by-
    // default cohort, which is exactly the cookieless, nothing-on-the-device state
    // doc/product-analytics.md §4 describes: captured, with a server-derived rotating id.
    opt_out_capturing_by_default: true,
    // Turbo Drive navigates through the History API. Leaving automatic capture on alongside the
    // turbo:load listener below is the classic double-count, so capture is ours alone.
    capture_pageview: false,
  }
}

// `leave` is called instead of `capture` when a navigation lands on a page with no settings element
// -- a page we do not measure. It exists because "do nothing" is the wrong answer once a session can
// be recording: the recorder has to be told, and this is the only moment anything hears about the
// reader having left the allow-list. It defaults to a no-op so a caller that starts nothing needs
// no answer for stopping it.
export function trackTurboPageviews({ eventTarget, documentRoot, initialize, capture, leave = () => {} }) {
  // Registering twice would capture two $pageviews per navigation, the exact failure the ticket's
  // acceptance criterion names ("not two"). Nothing in the markup can promise a single evaluation:
  // Turbo appends any head script whose outerHTML it has not seen before, so a deploy that changes
  // only the analytics bundle drops a second copy into the head of every open session, and Turbo
  // re-evaluates every body script on every visit. The guard is what makes one listener a fact.
  if (eventTarget[POST_HOG_LISTENER_MARKER]) return

  eventTarget[POST_HOG_LISTENER_MARKER] = true

  let started = false

  const onNavigation = () => {
    const settings = readPostHogSettings(documentRoot)
    // A page with no settings element is a page we do not measure. Reading it on every
    // navigation rather than once at start-up is what stops a Turbo visit from the marketing
    // site into the authenticated app from capturing a pageview off an app screen -- and, since
    // 12.6, from carrying a live recorder onto a payout screen.
    if (!settings) {
      if (started) leave()
      return
    }

    if (!started) {
      initialize(settings)
      started = true
    }

    capture(settings)
  }

  eventTarget.addEventListener("turbo:load", onNavigation)

  // THE VISIT THAT BROUGHT THIS BUNDLE IN HAS ALREADY FIRED ITS `turbo:load` BY NOW. Turbo
  // activates a body script and completes the visit without waiting for it, so a module fetched
  // over the network is evaluated after the one event it registers for. Every signed-in page that
  // carries this bundle carries it in the body (analytics/_post_hog_app.html.erb), and the ordinary
  // way an admin reaches both of them is a Turbo visit from another console page -- so without this
  // line the page that asked for the recorder is the one page it never runs on, and replay would
  // start only on the navigation after it.
  //
  // `complete` IS WHAT SEPARATES A TURBO VISIT FROM AN ORDINARY PAGE LOAD, and it is why this
  // cannot double-capture. A deferred module always runs while the document is still `interactive`,
  // and Turbo's own `turbo:load` for a full load arrives later than that, so on a first load this
  // is false and the listener above is the only thing that fires.
  if (documentRoot.readyState === "complete") onNavigation()
}
